import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Feedback } from "@/types/performance";
import { PaperPlaneRight, ThumbsUp, Lightbulb } from "@phosphor-icons/react";
import { toast } from "sonner";

export function Feedback360() {
  const [feedbackList, setFeedbackList] = useKV<Feedback[]>("performance-feedback", [
    {
      id: "fb-1",
      fromId: "user-2",
      fromName: "Jane Smith",
      toId: "CURRENT_USER",
      toName: "Me",
      message: "Great job leading the project last week! Your organization skills really shone through.",
      type: "Praise",
      isAnonymous: false,
      createdAt: new Date().toISOString()
    }
  ]);
  const [isGiveFeedbackOpen, setIsGiveFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackType, setFeedbackType] = useState<'Praise' | 'Constructive'>('Praise');

  const handleSendFeedback = () => {
    if (!feedbackMessage) return;

    const newFeedback: Feedback = {
      id: crypto.randomUUID(),
      fromId: "CURRENT_USER",
      fromName: "Me",
      toId: "user-x", // Mock recipient
      toName: "Colleague", 
      message: feedbackMessage,
      type: feedbackType,
      isAnonymous: false,
      createdAt: new Date().toISOString()
    };

    setFeedbackList([newFeedback, ...feedbackList]);
    setIsGiveFeedbackOpen(false);
    setFeedbackMessage("");
    toast.success("Feedback sent successfully");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Continuous Feedback</h3>
        <Button onClick={() => setIsGiveFeedbackOpen(true)}>
          <PaperPlaneRight className="mr-2 h-4 w-4" /> Give Feedback
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Received Feedback */}
        <Card>
          <CardHeader>
            <CardTitle>Received</CardTitle>
            <CardDescription>Feedback from your team</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {feedbackList.filter(f => f.toId === 'CURRENT_USER').map(fb => (
              <div key={fb.id} className="p-4 border rounded-lg bg-white dark:bg-slate-900/50 shadow-sm">
                <div className="flex items-start gap-3">
                  <Avatar>
                    <AvatarFallback>{fb.fromName[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{fb.fromName}</span>
                      <span className="text-xs text-slate-400">• {new Date(fb.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {fb.type === 'Praise' ? <ThumbsUp className="text-green-500 w-4 h-4" /> : <Lightbulb className="text-amber-500 w-4 h-4" />}
                      <span className="text-xs font-medium text-slate-500 uppercase">{fb.type}</span>
                    </div>
                    <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{fb.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Given Feedback */}
        <Card>
          <CardHeader>
            <CardTitle>Given</CardTitle>
            <CardDescription>Feedback you've shared</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {feedbackList.filter(f => f.fromId === 'CURRENT_USER').map(fb => (
              <div key={fb.id} className="p-4 border rounded-lg bg-slate-50 dark:bg-slate-900/30">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-medium">To: {fb.toName}</span>
                    <p className="text-sm mt-1 text-slate-600">{fb.message}</p>
                  </div>
                  <span className="text-xs text-slate-400">{new Date(fb.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
            {feedbackList.filter(f => f.fromId === 'CURRENT_USER').length === 0 && (
              <p className="text-center text-slate-500 py-8 text-sm">You haven't given any feedback yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={isGiveFeedbackOpen} onOpenChange={setIsGiveFeedbackOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Give Feedback</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Colleague</Label>
              <Input placeholder="Search for a colleague..." />
            </div>
            
            <div className="space-y-2">
              <Label>Type</Label>
              <div className="flex gap-2">
                <Button 
                  type="button"
                  variant={feedbackType === 'Praise' ? 'default' : 'outline'} 
                  className={feedbackType === 'Praise' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setFeedbackType('Praise')}
                >
                  <ThumbsUp className="mr-2" /> Praise
                </Button>
                <Button 
                  type="button"
                  variant={feedbackType === 'Constructive' ? 'default' : 'outline'}
                  className={feedbackType === 'Constructive' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                  onClick={() => setFeedbackType('Constructive')}
                >
                  <Lightbulb className="mr-2" /> Constructive
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea 
                placeholder={feedbackType === 'Praise' ? "What did they do well?" : "How can they improve?"}
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                className="h-32"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGiveFeedbackOpen(false)}>Cancel</Button>
            <Button onClick={handleSendFeedback}>Send Feedback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
